import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

type ToastIntent = 'success' | 'error' | 'warning' | 'info';

type Toast = {
  id: string;
  intent: ToastIntent;
  message: string;
};

type ToastContextValue = {
  show: (intent: ToastIntent, message: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

function generateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `toast-${Math.random().toString(36).slice(2, 10)}`;
}

const intentStyles: Record<ToastIntent, string> = {
  // High visibility: Solid backgrounds with white text for maximum contrast
  success: 'border-success-500 bg-success-500 text-text-primary shadow-soft-md shadow-success-500/20',
  error: 'border-error-500 bg-error-500 text-text-primary shadow-soft-md shadow-error-500/20',
  warning: 'border-warning-500 bg-warning-500 text-text-primary shadow-soft-md shadow-warning-500/20',
  info: 'border-brand-primary bg-brand-primary text-text-primary shadow-soft-md shadow-brand-primary/20'
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, number>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const handle = timers.current.get(id);
    if (handle) {
      window.clearTimeout(handle);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (intent: ToastIntent, message: string) => {
      const id = generateId();
      setToasts((prev) => [...prev, { id, intent, message }]);

      if (typeof window !== 'undefined') {
        const handle = window.setTimeout(() => {
          dismiss(id);
        }, 4500);
        timers.current.set(id, handle);
      }
    },
    [dismiss]
  );

  useEffect(() => {
    return () => {
      timers.current.forEach((handle) => {
        window.clearTimeout(handle);
      });
      timers.current.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="pointer-events-none fixed inset-x-0 top-4 z-50 flex flex-col items-center gap-2 px-4">
          <AnimatePresence mode="popLayout">
            {toasts.map((toast) => (
              <motion.div
                key={toast.id}
                initial={{ opacity: 0, y: -50, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, x: 100, scale: 0.9 }}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 40,
                  mass: 1,
                }}
                className={`pointer-events-auto w-full max-w-sm rounded-xl border px-4 py-3 text-sm shadow-soft-md backdrop-blur-sm relative overflow-hidden ${
                  intentStyles[toast.intent]
                }`}
                role={toast.intent === 'error' ? 'alert' : 'status'}
                aria-live={toast.intent === 'error' ? 'assertive' : 'polite'}
                aria-atomic="true"
                layout
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="font-medium">{toast.message}</span>
                  <motion.button
                    type="button"
                    className="text-xs uppercase tracking-wide text-text-secondary transition hover:text-text-primary font-semibold"
                    onClick={() => dismiss(toast.id)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    aria-label={`Dismiss ${toast.intent} notification`}
                  >
                    ✕
                  </motion.button>
                </div>
                {/* Auto-dismiss progress bar */}
                <motion.div
                  className="absolute bottom-0 start-0 h-1 bg-white/30 rounded-es-xl"
                  initial={{ width: '100%' }}
                  animate={{ width: '0%' }}
                  transition={{ duration: 4.5, ease: 'linear' }}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }

  const success = useCallback((message: string) => context.show('success', message), [context]);
  const error = useCallback((message: string) => context.show('error', message), [context]);
  const warning = useCallback((message: string) => context.show('warning', message), [context]);
  const info = useCallback((message: string) => context.show('info', message), [context]);

  return { success, error, warning, info } as const;
}

export type UseToastReturn = ReturnType<typeof useToast>;
